import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  redirect,
} from "react-router-dom";
import {
  apiClient,
  OccupancyBlocker,
  UnitDetailsLoaderResult,
  UnitDisplayAttributes,
  UnitPlanTableItem,
} from "~/services/apiClient";
import {
  _UnitPageTabType,
  FilesReturnType,
  FormattedServiceRequestData,
  LoaderData,
  NewLeaseFormPayload,
} from "~/types";
import {
  ENVS,
  INTENTS,
  getFilteredRequestList,
  loadCookiesData,
  resolveActorUserName,
  getAuthWrapper,
  getReadyAuthUser,
} from "~/utils";
import { withEntityCacheInvalidation } from "~/utils/loaderQueryCache";
import * as APITypes from "~/API";
import { S3ServiceLambda } from "~/services/s3";

export const UnitDetailsPageLoader = async ({
  request,
  params,
}: LoaderFunctionArgs): Promise<UnitDetailsPageLoaderType | Response> => {
  const auth = getAuthWrapper();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");
  const authAccessInfo = await loadCookiesData(user?.uid || "");
  if (!authAccessInfo.success) return redirect("/explore");
  const { ownerID, ownerType, role } = authAccessInfo.data;
  const { searchParams } = new URL(request.url);
  const unitID = params.unitID || "";

  const result = await apiClient.loader<UnitDetailsLoaderResult>(
    "unit-details",
    {
      id: unitID,
      ownerID,
      ownerType,
      userID: user.uid,
    },
  );

  if (!result.success || !result.data) {
    console.error("Failed to load unit details:", result.error);
    throw new Error(result.error ?? "Failed to load unit details");
  }

  const data = result.data;
  const isAdminOrSuperAdmin =
    role === APITypes.ManagerRolesType.SuperAdmin ||
    role === APITypes.ManagerRolesType.Admin;

  const formattedServiceRequestList = getFilteredRequestList({
    filters: {},
    requestList:
      data.formattedServiceRequestList as FormattedServiceRequestData[],
    isAdminOrSuperAdmin,
  });

  const userName = await resolveActorUserName(user, authAccessInfo);

  const files = await S3ServiceLambda.list({
    entityType: APITypes.EntityType.Unit,
    entityID: unitID,
  });

  return {
    tab: (searchParams.get("tab") ?? "details") as _UnitPageTabType,
    unitDetails: {
      success: true,
      data: data.unitDetails,
    } as LoaderData<APITypes.Unit>,
    buildingDetails: {
      success: true,
      data: data.buildingDetails,
    } as LoaderData<APITypes.Building>,
    propertyDetails: {
      success: true,
      data: data.propertyDetails,
    } as LoaderData<APITypes.Property>,
    formattedServiceRequestList,
    ownerID,
    ownerType,
    userName,
    files: files.data,
    tenantList: data.tenantList,
    canCreateRequest: data.permissions.canCreateServiceRequest,
    canDeleteUnit: data.permissions.canDeleteUnit,
    canEditUnit: data.permissions.canEditUnit,
    canReadUnit: data.permissions.canReadUnit,
    canReadUnitPlan: data.permissions.canReadUnitPlan,
    canCreateStay: data.permissions.canCreateStay ?? false,
    canCreateTenant: data.permissions.canCreateTenant ?? false,
    linkedUnitPlan: data.linkedUnitPlan,
    displayAttributes: data.displayAttributes,
    propertyUnitPlanList: data.propertyUnitPlanList ?? [],
    isAdminOrSuperAdmin,
    isAuthenticated: true,
    subscriptionPlan: data.subscriptionPlan,
    hasServiceRequestAccess: data.hasServiceRequestAccess,
    hasFinanceAccess: data.hasFinanceAccess,
    stays: data.stays ?? [],
    stayRate: data.stayRate ?? null,
    stayBlocks: data.stayBlocks ?? [],
    occupancyLeases: data.occupancyLeases ?? [],
  };
};

export type UnitDetailsPageLoaderType = {
  tab: _UnitPageTabType;
  unitDetails: LoaderData<APITypes.Unit>;
  buildingDetails: LoaderData<APITypes.Building>;
  propertyDetails: LoaderData<APITypes.Property>;
  formattedServiceRequestList: FormattedServiceRequestData[];
  ownerID: string;
  ownerType: APITypes.OwnerType;
  userName: string;
  files: FilesReturnType;
  tenantList: APITypes.Tenant[];
  canCreateRequest: boolean;
  canDeleteUnit: boolean;
  canEditUnit: boolean;
  canReadUnit: boolean;
  canReadUnitPlan: boolean;
  canCreateStay: boolean;
  canCreateTenant: boolean;
  linkedUnitPlan: APITypes.UnitPlan | null;
  displayAttributes: UnitDisplayAttributes;
  propertyUnitPlanList: UnitPlanTableItem[];
  isAdminOrSuperAdmin: boolean;
  isAuthenticated: boolean;
  subscriptionPlan: APITypes.SubscriptionPlan;
  hasServiceRequestAccess: boolean;
  hasFinanceAccess: boolean;
  stays: APITypes.Stay[];
  stayRate: APITypes.StayRate | null;
  stayBlocks: APITypes.StayAvailability[];
  occupancyLeases: APITypes.Lease[];
};

export const UnitDetailsPageAction = async ({
  request,
  params,
}: ActionFunctionArgs) => {
  const formData = await request.formData();
  const intent = formData.get("intent");
  const auth = getAuthWrapper();
  const user = await getReadyAuthUser();
  if (!user) return { success: false, error: "Not authenticated" };
  const unitID = params.unitID || "";
  const authAccessInfo = await loadCookiesData(user.uid);
  const ownerID =
    authAccessInfo.success && authAccessInfo.data
      ? authAccessInfo.data.ownerID
      : user.uid;
  const ownerType =
    authAccessInfo.success && authAccessInfo.data
      ? authAccessInfo.data.ownerType
      : APITypes.OwnerType.User;
  const userName = await resolveActorUserName(user, authAccessInfo);

  switch (intent) {
    case INTENTS._edit_unit: {
      return withEntityCacheInvalidation(
        [{ entity: "unit", entityID: unitID }],
        async () => {
          const payload = JSON.parse(formData.get("body") as string) as Record<
            string,
            unknown
          >;
          const result = await apiClient.sendIntent<APITypes.Unit>(
            INTENTS._edit_unit,
            { id: unitID, ...payload },
          );
          if (!result.success) {
            const err = result as {
              error?: string;
              debugLogs?: string[];
              code?: string;
              blockers?: OccupancyBlocker[];
            };
            return {
              success: false,
              data: {} as APITypes.Unit,
              error: err.error ?? "Failed to update unit",
              ...(Array.isArray(err.debugLogs) && { debugLogs: err.debugLogs }),
              ...(typeof err.code === "string" && { code: err.code }),
              ...(Array.isArray(err.blockers) && { blockers: err.blockers }),
            };
          }
          return result;
        },
      );
    }
    case INTENTS._add_service_request: {
      const payload = JSON.parse(formData.get("body") as string) as Record<
        string,
        unknown
      >;
      const serviceRequestPayload = {
        ...payload,
        creatorID: user.uid,
        creatorName: userName,
        ownerID,
        ownerType,
      };
      const result = await apiClient.sendIntent<APITypes.ServiceRequest>(
        INTENTS._add_service_request,
        serviceRequestPayload,
      );
      if (result.success && result.data) {
        return { success: true, data: result.data };
      }
      return {
        success: false,
        data: {} as APITypes.ServiceRequest,
        error: result.error ?? "Failed to create service request",
      };
    }
    case INTENTS._remove_unit: {
      return withEntityCacheInvalidation(
        [{ entity: "unit", entityID: unitID }],
        async () => {
          const bodyStr = formData.get("body") as string;
          const payload = bodyStr ? JSON.parse(bodyStr) : {};
          const result = await apiClient.sendIntent<{
            deleted: boolean;
            id: string;
          }>(INTENTS._remove_unit, { id: unitID, _version: payload._version });
          if (result.success) {
            return redirect("/unit");
          }
          return {
            success: false,
            error: result.error ?? "Failed to remove unit",
          };
        },
      );
    }
    case INTENTS._delete_file: {
      const key: string = formData.get("body") as string;
      const result = await S3ServiceLambda.remove({ key });
      return result;
    }
    case INTENTS._add_lease: {
      return withEntityCacheInvalidation(
        [
          { entity: "unit", entityID: unitID },
          { entity: "lease" },
        ],
        async () => {
          const payload = JSON.parse(
            formData.get("body") as string,
          ) as NewLeaseFormPayload;
          const leasePayload = {
            ...payload,
            entityID: unitID,
            ownerID,
            ownerType,
          };
          const result = await apiClient.sendIntent<APITypes.Lease>(
            INTENTS._add_lease,
            leasePayload,
          );
          if (result.success && result.data) {
            return { success: true, data: result.data };
          }
          return {
            success: false,
            data: {} as APITypes.Lease,
            error: result.error ?? "Failed to create lease",
          };
        },
      );
    }
    case INTENTS._add_stay: {
      return withEntityCacheInvalidation(
        [{ entity: "unit", entityID: unitID }],
        async () => {
          const payload = JSON.parse(formData.get("body") as string) as {
            tenantID?: string;
            checkIn?: string;
            checkOut?: string;
            notes?: string;
            nightlyRate?: number;
            totalAmount?: number;
            newGuest?: {
              firstName?: string;
              lastName?: string;
              email?: string;
              phoneNumber?: string;
            };
          };
          let tenantID = payload.tenantID?.trim() ?? "";
          if (!tenantID && payload.newGuest) {
            const guest = payload.newGuest;
            const tenantResult = await apiClient.sendIntent<APITypes.Tenant>(
              INTENTS._add_tenant,
              {
                type: APITypes.TenantType.ShadowTenant,
                firstName: guest.firstName,
                lastName: guest.lastName,
                email: guest.email,
                phoneNumber: guest.phoneNumber,
              },
            );
            if (!tenantResult.success || !tenantResult.data?.id) {
              return stayIntentFailure(
                tenantResult,
                "Failed to create guest tenant",
              );
            }
            tenantID = tenantResult.data.id;
          }
          const result = await apiClient.sendIntent<APITypes.Stay>(
            INTENTS._add_stay,
            {
              entityType: APITypes.EntityType.Unit,
              entityID: unitID,
              tenantID,
              checkIn: payload.checkIn,
              checkOut: payload.checkOut,
              notes: payload.notes,
              nightlyRate: payload.nightlyRate,
              totalAmount: payload.totalAmount,
              status: APITypes.StayStatusType.Confirmed,
              ownerID,
              ownerType,
            },
          );
          if (result.success && result.data) {
            return { success: true, data: result.data };
          }
          return stayIntentFailure(result, "Failed to create stay");
        },
      );
    }
    case INTENTS._update_stay: {
      return withEntityCacheInvalidation(
        [{ entity: "unit", entityID: unitID }],
        async () => {
          const payload = JSON.parse(formData.get("body") as string) as Record<
            string,
            unknown
          >;
          const result = await apiClient.sendIntent<APITypes.Stay>(
            INTENTS._update_stay,
            payload,
          );
          if (result.success && result.data) {
            return { success: true, data: result.data };
          }
          return stayIntentFailure(result, "Failed to update stay");
        },
      );
    }
    case INTENTS._request_stay_payment: {
      return withEntityCacheInvalidation(
        [{ entity: "unit", entityID: unitID }],
        async () => {
          const payload = JSON.parse(formData.get("body") as string) as Record<
            string,
            unknown
          >;
          const result = await apiClient.sendIntent<APITypes.Transaction>(
            INTENTS._request_stay_payment,
            payload,
          );
          if (result.success && result.data) {
            return { success: true, data: result.data };
          }
          return stayIntentFailure(result, "Failed to request stay payment");
        },
      );
    }
    case INTENTS._upsert_stay_rate: {
      return withEntityCacheInvalidation(
        [{ entity: "unit", entityID: unitID }],
        async () => {
          const payload = JSON.parse(formData.get("body") as string) as Record<
            string,
            unknown
          >;
          const result = await apiClient.sendIntent<APITypes.StayRate>(
            INTENTS._upsert_stay_rate,
            {
              entityType: APITypes.EntityType.Unit,
              entityID: unitID,
              ownerID,
              ownerType,
              ...payload,
            },
          );
          if (result.success && result.data) {
            return { success: true, data: result.data };
          }
          return stayIntentFailure(result, "Failed to save stay rate");
        },
      );
    }
    case INTENTS._add_stay_availability: {
      return withEntityCacheInvalidation(
        [{ entity: "unit", entityID: unitID }],
        async () => {
          const payload = JSON.parse(formData.get("body") as string) as Record<
            string,
            unknown
          >;
          const result = await apiClient.sendIntent<APITypes.StayAvailability>(
            INTENTS._add_stay_availability,
            {
              entityType: APITypes.EntityType.Unit,
              entityID: unitID,
              ownerID,
              ownerType,
              ...payload,
            },
          );
          if (result.success && result.data) {
            return { success: true, data: result.data };
          }
          return stayIntentFailure(result, "Failed to add blocked dates");
        },
      );
    }
    case INTENTS._remove_stay_availability: {
      return withEntityCacheInvalidation(
        [{ entity: "unit", entityID: unitID }],
        async () => {
          const payload = JSON.parse(formData.get("body") as string) as Record<
            string,
            unknown
          >;
          const result = await apiClient.sendIntent<{ id: string }>(
            INTENTS._remove_stay_availability,
            payload,
          );
          if (result.success) {
            return { success: true, data: result.data };
          }
          return stayIntentFailure(result, "Failed to remove blocked dates");
        },
      );
    }
  }
  return null;
};

function stayIntentFailure<T>(
  result: {
    error?: string;
    debugLogs?: string[];
    code?: string;
    blockers?: OccupancyBlocker[];
  },
  fallback: string,
) {
  return {
    success: false as const,
    error: result.error ?? fallback,
    ...(Array.isArray(result.debugLogs) && { debugLogs: result.debugLogs }),
    ...(typeof result.code === "string" && { code: result.code }),
    ...(Array.isArray(result.blockers) && { blockers: result.blockers }),
  };
}
